import type {AxiosError} from 'axios';
import axios from 'axios';
import {performApiRequest} from '@/store/helpers/vuex-helpers';
import type {
    ScheduledEmailQueueItem,
    ScheduledEmailQueueMeta,
    ScheduledEmailQueueSummary,
    ScheduledEmailTemplateOption
} from '@/types/scheduledEmail';
import type {StoreLoaders} from '@/types/common';
import {handleApiError} from '@/utils/errorHandler';

export interface ScheduledEmailsState {
    loading: StoreLoaders;
    scheduledEmails: ScheduledEmailQueueItem[];
    scheduledEmail: ScheduledEmailQueueItem | null;
    meta: ScheduledEmailQueueMeta | null;
    pendingCount: number;
    templateOptions: ScheduledEmailTemplateOption[];
}

const state: ScheduledEmailsState = {
    loading: {
        scheduled_emails: false,
    },
    scheduledEmails: [],
    scheduledEmail: null,
    meta: null,
    pendingCount: 0,
    templateOptions: [],
};

const getters = {
    loading: (state: ScheduledEmailsState) => state.loading,
    scheduledEmails: (state: ScheduledEmailsState) => state.scheduledEmails,
    scheduledEmail: (state: ScheduledEmailsState) => state.scheduledEmail,
    scheduledEmailsMeta: (state: ScheduledEmailsState) => state.meta,
    pendingCount: (state: ScheduledEmailsState) => state.pendingCount,
    templateOptions: (state: ScheduledEmailsState) => state.templateOptions,
};

const mutations = {
    SET_SCHEDULED_EMAILS_LOADING(state: ScheduledEmailsState, loading: { subject: string; loading: boolean }) {
        state.loading[loading.subject] = loading.loading;
    },
    SET_SCHEDULED_EMAILS(state: ScheduledEmailsState, scheduledEmails: ScheduledEmailQueueItem[]) {
        state.scheduledEmails = scheduledEmails;
    },
    SET_SCHEDULED_EMAIL(state: ScheduledEmailsState, scheduledEmail: ScheduledEmailQueueItem | null) {
        state.scheduledEmail = scheduledEmail;
    },
    SET_SCHEDULED_EMAILS_META(state: ScheduledEmailsState, meta: ScheduledEmailQueueMeta | null) {
        state.meta = meta;
    },
    SET_SCHEDULED_EMAILS_SUMMARY(state: ScheduledEmailsState, summary: ScheduledEmailQueueSummary) {
        state.pendingCount = summary.pending_count;
        state.templateOptions = summary.template_options || [];
    },
};

const actions = {
    async fetchForBooking({commit}, bId: string) {
        try {
            const responseData = await performApiRequest(
                commit,
                'SET_SCHEDULED_EMAILS_LOADING',
                'scheduled_emails',
                () => axios.get(`/v1/bookings/${bId}/scheduled-emails`),
            );
            commit('SET_SCHEDULED_EMAILS', responseData.scheduledEmails || []);

            return responseData.scheduledEmails || [];
        } catch (error) {
            handleApiError(error, 'There was an error fetching scheduled emails');
        }
    },

    async fetchQueue({commit}, params: {
        status?: string;
        identifier?: string;
        date_range?: string;
        per_page?: number;
        page?: number;
    } = {}) {
        const query = new URLSearchParams();

        if (params.status) {
            query.set('status', params.status);
        }

        if (params.identifier) {
            query.set('identifier', params.identifier);
        }

        if (params.date_range) {
            query.set('date_range', params.date_range);
        }

        if (params.per_page) {
            query.set('per_page', String(params.per_page));
        }

        if (params.page) {
            query.set('page', String(params.page));
        }

        const qString = query.toString() ? `?${query.toString()}` : '';

        try {
            const responseData = await performApiRequest(
                commit,
                'SET_SCHEDULED_EMAILS_LOADING',
                'scheduled_emails',
                () => axios.get(`/v1/scheduled-emails${qString}`),
            );
            commit('SET_SCHEDULED_EMAILS', responseData.scheduledEmails || []);
            commit('SET_SCHEDULED_EMAILS_META', responseData.meta || null);

            return responseData;
        } catch (error) {
            handleApiError(error, 'There was an error fetching the email queue');
        }
    },

    async fetchSummary({commit}) {
        try {
            const response = await axios.get('/v1/scheduled-emails/summary');

            commit('SET_SCHEDULED_EMAILS_SUMMARY', response.data);

            return response.data as ScheduledEmailQueueSummary;
        } catch (error) {
            handleApiError(error, 'There was an error fetching the email queue summary');
        }
    },

    async fetchOne({commit}, uuid: string) {
        try {
            const responseData = await performApiRequest(
                commit,
                'SET_SCHEDULED_EMAILS_LOADING',
                'scheduled_emails',
                () => axios.get(`/v1/scheduled-emails/${uuid}`),
            );
            commit('SET_SCHEDULED_EMAIL', responseData.scheduledEmail || null);

            return responseData.scheduledEmail;
        } catch (error) {
            handleApiError(error, 'There was an error fetching the scheduled email');
        }
    },

    async updateContent({commit}, payload: { uuid: string; subject: string; email_body_blocks: unknown }) {
        try {
            const responseData = await performApiRequest(
                commit,
                'SET_SCHEDULED_EMAILS_LOADING',
                'scheduled_emails',
                () => axios.put(`/v1/scheduled-emails/${payload.uuid}`, {
                    subject: payload.subject,
                    email_body_blocks: payload.email_body_blocks,
                }),
            );
            commit('SET_SCHEDULED_EMAIL', responseData.scheduledEmail || null);

            return responseData.scheduledEmail;
        } catch (error) {
            handleApiError(error, 'There was an error saving the scheduled email');
        }
    },

    async reschedule({commit}, payload: { uuid: string; scheduled_for: string }) {
        try {
            const responseData = await performApiRequest(
                commit,
                'SET_SCHEDULED_EMAILS_LOADING',
                'scheduled_emails',
                () => axios.patch(`/v1/scheduled-emails/${payload.uuid}/reschedule`, {
                    scheduled_for: payload.scheduled_for,
                }),
            );

            return responseData.scheduledEmail;
        } catch (error) {
            handleApiError(error, 'There was an error rescheduling the email');
        }
    },

    async skip({commit}, uuid: string) {
        try {
            const responseData = await performApiRequest(
                commit,
                'SET_SCHEDULED_EMAILS_LOADING',
                'scheduled_emails',
                () => axios.post(`/v1/scheduled-emails/${uuid}/skip`),
            );

            return responseData.scheduledEmail;
        } catch (error) {
            handleApiError(error, 'There was an error skipping the scheduled email');
        }
    },

    async sendNow({commit}, uuid: string) {
        try {
            const responseData = await performApiRequest(
                commit,
                'SET_SCHEDULED_EMAILS_LOADING',
                'scheduled_emails',
                () => axios.post(`/v1/scheduled-emails/${uuid}/send-now`),
            );

            return responseData.scheduledEmail;
        } catch (error) {
            const err = error as AxiosError;
            handleApiError(error, (err.response?.data as {
                message?: string
            })?.message || 'There was an error sending the email');
        }
    },

    async resetContent({commit}, uuid: string) {
        try {
            const responseData = await performApiRequest(
                commit,
                'SET_SCHEDULED_EMAILS_LOADING',
                'scheduled_emails',
                () => axios.post(`/v1/scheduled-emails/${uuid}/reset-content`),
            );
            commit('SET_SCHEDULED_EMAIL', responseData.scheduledEmail || null);

            return responseData.scheduledEmail;
        } catch (error) {
            handleApiError(error, 'There was an error resetting the scheduled email');
        }
    },

    async preview(_ctx, payload: { uuid: string; subject?: string; email_body_blocks?: unknown }) {
        try {
            const response = await axios.post(`/v1/scheduled-emails/${payload.uuid}/preview`, {
                subject: payload.subject,
                email_body_blocks: payload.email_body_blocks,
            });

            return response.data.preview;
        } catch (error) {
            handleApiError(error, 'There was an error loading the email preview');

            throw error;
        }
    },

    async unskip({commit}, uuid: string) {
        try {
            const responseData = await performApiRequest(
                commit,
                'SET_SCHEDULED_EMAILS_LOADING',
                'scheduled_emails',
                () => axios.post(`/v1/scheduled-emails/${uuid}/unskip`),
            );

            return responseData.scheduledEmail;
        } catch (error) {
            const err = error as AxiosError;
            handleApiError(error, (err.response?.data as {
                message?: string
            })?.message || 'There was an error restoring the scheduled email');
        }
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
